import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { AppShell } from "@/components/AppShell";
import { meQuery, requireAuth } from "@/lib/auth-guard";
import type { UserResponse } from "@/api/auth";
import { createPortalSession, tierName } from "@/api/billing";
import {
  cancelDataExport,
  deleteAccount,
  estimateDataExport,
  getDeletionImpact,
  requestDataExport,
  DELETE_CONFIRM_PHRASE,
  type DataExportEstimate,
  type DataExportRequestBody,
  type DataExportScope,
  type DataExportStatus,
  type DeletionImpactResponse,
} from "@/api/account";
import { extractApiError, httpStatus } from "@/api/client";
import { dataExportsQuery, subscriptionQuery } from "@/lib/queries";
import { formatBytes, formatDate } from "@/lib/format";
import { totpCode, totpSecondsLeft } from "@/lib/totp";
import {
  Card,
  DangerButton,
  ErrorText,
  Hairline,
  InkButton,
  LoadingBlock,
  QuietButton,
  RadioRow,
  SectionLabel,
} from "@/components/ui";
import { Modal } from "@/components/Modal";

/**
 * Account — mirrors the iOS ACCOUNT section (Subscription + Data &
 * Privacy). Subscription is read-only on web (purchases stay in iOS
 * StoreKit). Data export is the full request → poll → ready flow with
 * the rotating access code. Account deletion is the canonical D-AD.1
 * flow: impact preview, consequences, typed confirmation, 30-day
 * grace.
 */

const AccountPage = () => {
  const { data: user } = useSuspenseQuery<UserResponse>(meQuery);
  return (
    <AppShell user={user}>
      <AccountView user={user} />
    </AppShell>
  );
};

const AccountView = ({ user }: { user: UserResponse }) => (
  <div className="mx-auto max-w-2xl">
    <header>
      <h1
        className="font-serif text-[28px] leading-[34px] text-ink"
        style={{ fontWeight: 600 }}
      >
        Account
      </h1>
      <p className="mt-1 font-serif text-[14px] italic text-ink-soft">
        Your data, your terms. Memily isn't an ad business. Your
        conversations and memories aren't shared with third parties.
      </p>
    </header>

    <SubscriptionSection />
    <ExportSection user={user} />
    <PrivacySection user={user} />
  </div>
);

// ── Subscription (read-only on web) ─────────────────────────────────

const SubscriptionSection = () => {
  const subscription = useQuery(subscriptionQuery());

  return (
    <section className="mt-8">
      <SectionLabel>Subscription</SectionLabel>
      {subscription.isPending ? (
        <Card className="p-6">
          <p className="font-serif text-[14px] italic text-ink-soft">
            Loading your subscription…
          </p>
        </Card>
      ) : subscription.isError ? (
        <Card className="p-6">
          <p className="font-serif text-[14px] italic text-ink-soft">
            We couldn't load your subscription. Please try again.
          </p>
        </Card>
      ) : (
        <Card className="p-6">
          <p className="font-sans text-[16px] font-medium text-ink">
            You're on {tierName(subscription.data.tier)}
            {subscription.data.tier_source === "comp" && " (comp account)"}
            {subscription.data.tier_source === "admin_grant" &&
              " (admin grant)"}
          </p>
          {subscription.data.current_period_end && (
            <p className="mt-1 font-sans text-[13px] text-ink-soft">
              {subscription.data.status === "cancelled"
                ? `Cancelled — ends ${formatDate(subscription.data.current_period_end)}`
                : subscription.data.status === "past_due"
                  ? `Payment retrying — ends ${formatDate(subscription.data.current_period_end)} if unresolved`
                  : `Renews ${formatDate(subscription.data.current_period_end)}`}
            </p>
          )}

          <div className="mt-3">
            {subscription.data.active_rail === "stripe" ? (
              <ManagePlanButton />
            ) : (
              <QuietButton
                onClick={() => {
                  window.location.href = "/plans";
                }}
              >
                See plans
              </QuietButton>
            )}
          </div>

          <div className="mt-5 flex flex-col gap-4">
            <UsageBar
              label="Photos"
              used={subscription.data.entitlements.pictures.used}
              cap={subscription.data.entitlements.pictures.cap}
            />
            <UsageBar
              label="Videos"
              used={subscription.data.entitlements.videos.used}
              cap={subscription.data.entitlements.videos.cap}
            />
            <UsageBar
              label="Seats"
              used={subscription.data.entitlements.seats.used}
              cap={subscription.data.entitlements.seats.cap}
            />
          </div>

          <p className="mt-5 font-sans text-[13px] text-ink-soft">
            To change your plan, open Memily on your iPhone — subscriptions
            are managed there for now.
          </p>
        </Card>
      )}
    </section>
  );
};

/** Stripe Customer Portal hop (webBillingBuild P-WB.4 / D-WB.2). */
const ManagePlanButton = () => {
  const portal = useMutation({
    mutationFn: createPortalSession,
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });
  return (
    <InkButton onClick={() => portal.mutate()} disabled={portal.isPending}>
      {portal.isPending ? "Opening…" : "Manage plan"}
    </InkButton>
  );
};

const UsageBar = ({
  label,
  used,
  cap,
}: {
  label: string;
  used: number | null;
  cap: number | null;
}) => {
  const pct =
    cap && cap > 0 && used !== null
      ? Math.min(100, Math.round((used / cap) * 100))
      : 0;
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="font-sans text-[13px] font-medium text-ink">
          {label}
        </span>
        <span className="font-sans text-[13px] text-ink-soft">
          {used ?? 0}
          {cap === null ? " · unlimited" : ` of ${cap}`}
        </span>
      </div>
      {cap !== null && (
        <div className="mt-1.5 h-1 w-full rounded-full bg-sand/60">
          <div
            className={`h-1 rounded-full ${pct >= 90 ? "bg-blush" : "bg-taupe"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
};

// ── Data export ─────────────────────────────────────────────────────

const SCOPES: Array<{ value: DataExportScope; label: string; sub: string }> = [
  { value: "last_week", label: "Last week", sub: "" },
  { value: "last_month", label: "Last month", sub: "" },
  { value: "last_year", label: "Last year", sub: "" },
  { value: "all_time", label: "All time", sub: "Everything in your Memily." },
  { value: "custom_range", label: "Custom range", sub: "" },
];

const ExportSection = ({ user }: { user: UserResponse }) => {
  const hasExport = user.resolved_features["DATA_EXPORT"] === true;
  const queryClient = useQueryClient();
  const exports = useQuery({
    ...dataExportsQuery,
    enabled: hasExport,
  });

  const active = useMemo(
    () =>
      (exports.data ?? []).find(
        (row) =>
          row.state === "queued" ||
          row.state === "processing" ||
          row.state === "ready",
      ) ?? null,
    [exports.data],
  );
  const inFlight =
    active?.state === "queued" || active?.state === "processing";

  // Poll while the worker builds the export (2s, matching iOS).
  useEffect(() => {
    if (!inFlight) return;
    const t = setInterval(
      () => void queryClient.invalidateQueries({ queryKey: ["data-exports"] }),
      2000,
    );
    return () => clearInterval(t);
  }, [inFlight, queryClient]);

  return (
    <section className="mt-8">
      <SectionLabel>Your data</SectionLabel>
      <Card className="p-6">
        <p className="font-serif text-[14px] italic leading-[22px] text-ink-soft">
          A complete copy of your Memily — every post, conversation,
          archive item, setting. Delivered as a signed download link when
          ready.
        </p>

        {!hasExport ? (
          <div className="mt-4">
            <p className="font-sans text-[15px] font-medium text-ink">
              Data export is a Premium feature.
            </p>
            <p className="mt-1 font-serif text-[13px] italic text-ink-soft">
              Upgrade your plan to download a copy of everything in your
              Memily — posts, conversations, archive items, and settings.
            </p>
          </div>
        ) : exports.isPending ? (
          <LoadingBlock label="Checking your exports…" />
        ) : active?.state === "ready" ? (
          <ReadyExport
            row={active}
            onChanged={() =>
              void queryClient.invalidateQueries({
                queryKey: ["data-exports"],
              })
            }
          />
        ) : inFlight && active ? (
          <InFlightExport
            row={active}
            onChanged={() =>
              void queryClient.invalidateQueries({
                queryKey: ["data-exports"],
              })
            }
          />
        ) : (
          <RequestExport
            onChanged={() =>
              void queryClient.invalidateQueries({
                queryKey: ["data-exports"],
              })
            }
          />
        )}
      </Card>
    </section>
  );
};

const toRequestBody = (
  scope: DataExportScope,
  rangeStart: string,
  rangeEnd: string,
): DataExportRequestBody =>
  scope === "custom_range"
    ? {
        scope,
        range_start: new Date(`${rangeStart}T00:00:00Z`).toISOString(),
        range_end: new Date(`${rangeEnd}T23:59:59Z`).toISOString(),
      }
    : { scope };

const RequestExport = ({ onChanged }: { onChanged: () => void }) => {
  const [scope, setScope] = useState<DataExportScope>("all_time");
  const [rangeStart, setRangeStart] = useState(() => {
    const d = new Date(Date.now() - 30 * 86_400_000);
    return d.toISOString().slice(0, 10);
  });
  const [rangeEnd, setRangeEnd] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [estimate, setEstimate] = useState<DataExportEstimate | null>(null);
  const [error, setError] = useState<string | null>(null);

  const rangeValid =
    scope !== "custom_range" || (!!rangeStart && !!rangeEnd && rangeStart <= rangeEnd);

  // Debounced estimate — refreshes 300ms after scope/range settles.
  useEffect(() => {
    if (!rangeValid) return;
    setEstimate(null);
    const t = setTimeout(() => {
      estimateDataExport(toRequestBody(scope, rangeStart, rangeEnd))
        .then(setEstimate)
        .catch(() => setEstimate(null));
    }, 300);
    return () => clearTimeout(t);
  }, [scope, rangeStart, rangeEnd, rangeValid]);

  const request = useMutation({
    mutationFn: () =>
      requestDataExport(toRequestBody(scope, rangeStart, rangeEnd)),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (err) => {
      const status = httpStatus(err);
      if (status === 422) setError("That range has no posts to export.");
      else if (status === 429)
        setError(
          "You've hit the rate limit for this scope. Try a smaller range, or wait.",
        );
      else if (status === 409)
        setError("An export is already in progress. Cancel it first.");
      else if (status === 402)
        setError("Data export is a Premium feature. Upgrade to use it.");
      else setError("Couldn't request an export. Try again in a moment.");
    },
  });

  return (
    <div className="mt-4">
      <div
        role="radiogroup"
        aria-label="What to export"
        className="overflow-hidden rounded-2xl border border-sand/40"
      >
        {SCOPES.map((s, i) => (
          <div key={s.value}>
            {i > 0 && <Hairline />}
            <RadioRow
              label={s.label}
              sub={s.sub || undefined}
              selected={scope === s.value}
              onSelect={() => setScope(s.value)}
            />
          </div>
        ))}
      </div>

      {scope === "custom_range" && (
        <div className="mt-4 flex flex-wrap gap-4">
          <label className="font-sans text-[13px] text-ink-soft">
            From
            <input
              type="date"
              value={rangeStart}
              onChange={(e) => setRangeStart(e.target.value)}
              className="tap-target mt-1 block rounded-2xl border border-sand bg-paper px-4 py-2 font-sans text-[15px] text-ink outline-none focus:border-taupe"
            />
          </label>
          <label className="font-sans text-[13px] text-ink-soft">
            To
            <input
              type="date"
              value={rangeEnd}
              onChange={(e) => setRangeEnd(e.target.value)}
              className="tap-target mt-1 block rounded-2xl border border-sand bg-paper px-4 py-2 font-sans text-[15px] text-ink outline-none focus:border-taupe"
            />
          </label>
        </div>
      )}

      {estimate && (
        <div className="mt-4 rounded-2xl bg-paper p-4">
          <p className="font-sans text-[14px] font-medium text-ink">
            {estimate.photo_count} photo
            {estimate.photo_count === 1 ? "" : "s"} · {estimate.video_count}{" "}
            video{estimate.video_count === 1 ? "" : "s"} ·{" "}
            {estimate.post_count} post
            {estimate.post_count === 1 ? "" : "s"}
          </p>
          <p className="mt-0.5 font-sans text-[13px] text-ink-soft">
            About {formatBytes(estimate.total_bytes)} across{" "}
            {estimate.estimated_parts} part
            {estimate.estimated_parts === 1 ? "" : "s"}
          </p>
          {estimate.next_allowed_at &&
            new Date(estimate.next_allowed_at) > new Date() && (
              <p className="mt-1 font-sans text-[13px] text-blush">
                You can request this scope again after{" "}
                {formatDate(estimate.next_allowed_at)}.
              </p>
            )}
        </div>
      )}

      <ErrorText>{error}</ErrorText>

      <div className="mt-5">
        <InkButton
          onClick={() => request.mutate()}
          disabled={request.isPending || !rangeValid}
        >
          {request.isPending ? "Requesting…" : "Request export"}
        </InkButton>
        <p className="mt-2 font-sans text-[12px] text-ink-soft">
          Premium feature · cancellable anytime
        </p>
      </div>
    </div>
  );
};

const InFlightExport = ({
  row,
  onChanged,
}: {
  row: DataExportStatus;
  onChanged: () => void;
}) => {
  const [error, setError] = useState<string | null>(null);
  const parts = row.part_manifest?.length ?? 0;
  const total = row.estimated_part_count ?? 0;
  const pct = total > 0 ? Math.min(100, Math.round((parts / total) * 100)) : 0;

  const cancel = useMutation({
    mutationFn: () => cancelDataExport(row.export_id),
    onSuccess: () => {
      setError(null);
      onChanged();
    },
    onError: (err) => {
      if (httpStatus(err) === 404) {
        onChanged();
        return;
      }
      setError("Couldn't cancel right now. Please try again.");
    },
  });

  return (
    <div className="mt-4">
      <div className="flex items-baseline justify-between">
        <p className="font-sans text-[15px] font-medium text-ink">
          {row.state === "queued" ? "Queued" : "Building your export"}
        </p>
        {total > 0 && (
          <p className="font-mono text-[12px] uppercase tracking-[0.14em] text-taupe-deep">
            {pct}% · {parts} of {total}
          </p>
        )}
      </div>
      <div className="mt-2 h-1.5 w-full rounded-full bg-sand/60">
        <div
          className="h-1.5 rounded-full bg-taupe transition-all"
          style={{ width: `${Math.max(4, pct)}%` }}
        />
      </div>
      <p className="mt-2 font-sans text-[13px] text-ink-soft">
        {row.state === "queued"
          ? "Queued behind the worker — starting in a moment."
          : "Memily is walking your data, gathering posts and media."}
      </p>
      <ErrorText>{error}</ErrorText>
      <div className="mt-4">
        <DangerButton
          onClick={() => cancel.mutate()}
          disabled={cancel.isPending}
        >
          {cancel.isPending ? "Cancelling…" : "Cancel"}
        </DangerButton>
        <p className="mt-2 font-sans text-[12px] text-ink-soft">
          Cancelling now doesn't count against your rate limit. You can
          request a fresh export immediately.
        </p>
      </div>
    </div>
  );
};

const ReadyExport = ({
  row,
  onChanged,
}: {
  row: DataExportStatus;
  onChanged: () => void;
}) => {
  const [confirming, setConfirming] = useState<"replace" | "delete" | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  const cancel = useMutation({
    mutationFn: () => cancelDataExport(row.export_id),
    onSuccess: () => {
      setConfirming(null);
      setError(null);
      onChanged();
    },
    onError: (err) => {
      setConfirming(null);
      if (httpStatus(err) === 404) {
        onChanged();
        return;
      }
      setError("Couldn't do that right now. Please try again.");
    },
  });

  const daysLeft = row.expires_at
    ? Math.max(
        0,
        Math.ceil(
          (new Date(row.expires_at).getTime() - Date.now()) / 86_400_000,
        ),
      )
    : null;

  return (
    <div className="mt-4">
      <p className="font-serif text-[18px] text-ink">Your export is ready.</p>
      <p className="mt-1 font-sans text-[13px] text-ink-soft">
        Open the link Memily emailed you, then enter the code below to
        start downloading.
        {row.expires_at &&
          ` Available until ${formatDate(row.expires_at)}${daysLeft !== null ? ` · ${daysLeft} day${daysLeft === 1 ? "" : "s"} left` : ""}.`}
      </p>

      {row.totp_secret && <RotatingCode secret={row.totp_secret} />}

      <ErrorText>{error}</ErrorText>

      <div className="mt-5 flex flex-wrap gap-3">
        <QuietButton onClick={() => setConfirming("replace")}>
          Request a new export
        </QuietButton>
        <DangerButton onClick={() => setConfirming("delete")}>
          Delete this export
        </DangerButton>
      </div>

      {confirming === "replace" && (
        <ConfirmInline
          title="Replace this export?"
          body="Your current download link will stop working and the access code will change. Your downloaded files (if any) stay on your device."
          confirmLabel="Replace"
          busy={cancel.isPending}
          onConfirm={() => cancel.mutate()}
          onCancel={() => setConfirming(null)}
        />
      )}
      {confirming === "delete" && (
        <ConfirmInline
          title="Delete this export?"
          body="The download link and access code stop working and the files are removed from Memily's servers. You can request a new export anytime."
          confirmLabel="Delete this export"
          busy={cancel.isPending}
          onConfirm={() => cancel.mutate()}
          onCancel={() => setConfirming(null)}
        />
      )}
    </div>
  );
};

// Lightweight inline confirm — avoids stacking a modal inside the
// export card for the two cancel flavors.
const ConfirmInline = ({
  title,
  body,
  confirmLabel,
  busy,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) => (
  <div className="mt-4 rounded-2xl border border-blush/30 bg-blush/5 p-4">
    <p className="font-sans text-[15px] font-medium text-ink">{title}</p>
    <p className="mt-1 font-sans text-[13px] leading-[20px] text-ink-soft">
      {body}
    </p>
    <div className="mt-3 flex gap-3">
      <QuietButton onClick={onCancel} disabled={busy}>
        Keep it
      </QuietButton>
      <DangerButton onClick={onConfirm} disabled={busy}>
        {busy ? "Working…" : confirmLabel}
      </DangerButton>
    </div>
  </div>
);

const RotatingCode = ({ secret }: { secret: string }) => {
  const [code, setCode] = useState("······");
  const [secondsLeft, setSecondsLeft] = useState(totpSecondsLeft());

  useEffect(() => {
    let alive = true;
    const tick = () => {
      setSecondsLeft(totpSecondsLeft());
      totpCode(secret)
        .then((c) => {
          if (alive) setCode(c);
        })
        .catch(() => {
          if (alive) setCode("——");
        });
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [secret]);

  return (
    <div className="mt-4 flex items-center gap-4 rounded-2xl bg-paper p-4">
      <p
        className="font-mono text-[28px] tracking-[0.3em] text-ink"
        aria-label="Your download access code"
      >
        {code}
      </p>
      <p className="font-sans text-[12px] text-ink-soft">
        New code in {secondsLeft}s
      </p>
    </div>
  );
};

// ── Privacy: policy links + delete account ──────────────────────────

const PrivacySection = ({ user }: { user: UserResponse }) => {
  const [deleting, setDeleting] = useState(false);

  return (
    <section className="mt-8">
      <SectionLabel>Data &amp; privacy</SectionLabel>
      <Card>
        <a
          href="https://memily.com/privacy"
          target="_blank"
          rel="noreferrer"
          className="tap-target flex items-center px-5 py-4 font-sans text-[15px] font-medium text-ink hover:bg-paper/60"
        >
          Privacy policy
        </a>
        <Hairline />
        <a
          href="https://memily.com/terms"
          target="_blank"
          rel="noreferrer"
          className="tap-target flex items-center px-5 py-4 font-sans text-[15px] font-medium text-ink hover:bg-paper/60"
        >
          Terms of service
        </a>
      </Card>

      {!user.is_minor && (
        <div className="mt-8">
          <p className="mb-3 font-serif text-[13px] italic leading-[20px] text-ink-soft">
            Delete your account anytime — it stays recoverable for 30 days:
            sign back in on your iPhone and choose Restore.
          </p>
          <DangerButton onClick={() => setDeleting(true)}>
            Delete account
          </DangerButton>
        </div>
      )}

      {deleting && <DeleteAccountFlow onClose={() => setDeleting(false)} />}
    </section>
  );
};

const DeleteAccountFlow = ({ onClose }: { onClose: () => void }) => {
  const [impact, setImpact] = useState<DeletionImpactResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [ackApple, setAckApple] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getDeletionImpact()
      .then(setImpact)
      .catch(() =>
        setLoadError(
          "We couldn't check what deleting your account would affect. Please try again.",
        ),
      );
  }, []);

  const del = useMutation({
    mutationFn: deleteAccount,
    onSuccess: () => {
      // Session is dead server-side; land on the signed-out page.
      window.location.href = "/";
    },
    onError: (err) =>
      setError(
        extractApiError(err)?.message ??
          "Couldn't delete your account right now. Please try again.",
      ),
  });

  const phraseOk =
    phrase.trim().toLowerCase() === DELETE_CONFIRM_PHRASE;
  const appleOk = !impact?.active_apple_subscription || ackApple;

  return (
    <Modal title="Delete your account" onClose={onClose} wide>
      {!impact && !loadError && (
        <LoadingBlock label="Checking what this affects…" />
      )}
      {loadError && <ErrorText>{loadError}</ErrorText>}

      {impact && (
        <div>
          <p className="font-sans text-[14px] leading-[22px] text-ink-soft">
            Your account is deleted right away and stays recoverable for 30
            days — sign back in on your iPhone and choose Restore. After 30
            days, everything is permanently removed.
          </p>

          {impact.owned_circles.length > 0 && (
            <div className="mt-4">
              <SectionLabel>The circles you own</SectionLabel>
              <Card className="border border-sand/40">
                {impact.owned_circles.map((c, i) => (
                  <div key={c.family_id}>
                    {i > 0 && <Hairline />}
                    <div className="px-5 py-4">
                      <p className="font-sans text-[15px] font-medium text-ink">
                        {c.family_name ?? "Your circle"}
                      </p>
                      {c.minors_deleted.length > 0 && (
                        <p className="mt-1 font-sans text-[13px] text-ink-soft">
                          The children's accounts here go with yours:{" "}
                          {c.minors_deleted.join(", ")}.
                        </p>
                      )}
                      {c.adults_relocated.length > 0 && (
                        <p className="mt-1 font-sans text-[13px] text-ink-soft">
                          {c.adults_relocated.join(", ")} will move to their
                          own circle{c.adults_relocated.length === 1 ? "" : "s"}.
                        </p>
                      )}
                      {c.transfer_available && (
                        <p className="mt-1 font-sans text-[13px] text-taupe-deep">
                          To preserve this circle instead, transfer
                          ownership first — from your Immediate Family
                          Circle page.
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </Card>
            </div>
          )}

          {impact.active_apple_subscription && (
            <label className="tap-target mt-4 flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={ackApple}
                onChange={(e) => setAckApple(e.target.checked)}
                className="mt-1 h-5 w-5 accent-[#8A7460]"
              />
              <span className="font-sans text-[14px] leading-[22px] text-ink-soft">
                I understand deleting my account does not cancel my Apple
                subscription — I'll cancel it in iOS Settings › Apple ID ›
                Subscriptions.
              </span>
            </label>
          )}

          <div className="mt-5">
            <label className="block">
              <span className="font-sans text-[14px] font-medium text-ink">
                To continue, type{" "}
                <span className="font-mono text-[13px]">
                  {DELETE_CONFIRM_PHRASE}
                </span>
              </span>
              <input
                type="text"
                value={phrase}
                onChange={(e) => setPhrase(e.target.value)}
                autoComplete="off"
                className="tap-target mt-2 w-full rounded-2xl border border-sand bg-paper px-4 py-3 font-sans text-[15px] outline-none focus:border-taupe"
              />
            </label>
          </div>

          <ErrorText>{error}</ErrorText>

          <div className="mt-6 flex justify-end gap-3">
            <QuietButton onClick={onClose}>Keep my account</QuietButton>
            <DangerButton
              onClick={() => del.mutate()}
              disabled={!phraseOk || !appleOk || del.isPending}
            >
              {del.isPending ? "Deleting…" : "Delete my account"}
            </DangerButton>
          </div>
        </div>
      )}
    </Modal>
  );
};

export const Route = createFileRoute("/account")({
  loader: ({ context }) => requireAuth(context.queryClient),
  component: AccountPage,
});
